import React, { useEffect, useRef, useState } from 'react';


// Google Maps API Key
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";



const shipmentData = {
    origin: { lat: 22.3193, lng: 114.1694 }, // Hong Kong
    destination: { lat: 52.3759, lng: 9.7320 }, // Hannover
    current: { lat: 12.0, lng: 45.0 }, // Gulf of Aden (Approaching Red Sea) - Adjusted slightly
    // Waypoints for Sea Route (HK -> Singapore -> Suez -> Gibraltar -> North Sea)
    pathPoints: [
        { lat: 22.3193, lng: 114.1694 }, // HK
        { lat: 18.0, lng: 115.0 }, // South China Sea
        { lat: 4.0, lng: 108.0 }, // Natuna Sea
        { lat: 1.2, lng: 104.0 }, // Singapore
        { lat: 5.5, lng: 95.0 }, // Malacca Exit
        { lat: 5.8, lng: 80.0 }, // Sri Lanka South
        { lat: 9.0, lng: 65.0 }, // Arabian Sea
        { lat: 12.0, lng: 45.0 }, // Gulf of Aden (Current Position)
        { lat: 12.5, lng: 43.5 }, // Bab el-Mandeb
        { lat: 20.0, lng: 39.0 }, // Red Sea
        { lat: 29.5, lng: 32.5 }, // Suez Canal South
        { lat: 31.5, lng: 32.5 }, // Suez Canal North
        { lat: 34.0, lng: 25.0 }, // Med East
        { lat: 37.0, lng: 11.0 }, // Med Sicily
        { lat: 36.5, lng: -4.0 }, // Gibraltar Approach
        { lat: 36.0, lng: -5.6 }, // Gibraltar Strait
        { lat: 40.0, lng: -10.0 }, // Portugal Coast
        { lat: 45.0, lng: -8.0 }, // Bay of Biscay
        { lat: 49.0, lng: -5.0 }, // English Channel Entry
        { lat: 51.0, lng: 1.5 }, // Dover
        { lat: 53.0, lng: 3.5 }, // North Sea
        { lat: 54.0, lng: 8.0 }, // German Bight
        { lat: 53.5, lng: 8.5 }, // Weser/Elbe Estuary
        { lat: 52.3759, lng: 9.7320 } // Hannover (Land leg assumption: Short transport or port)
    ]
};

export const LiveTrackingMap = ({ products = [] }) => {
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const [map, setMap] = useState(null);
    // Initialize state based on current global availability
    const [scriptLoaded, setScriptLoaded] = useState(() => !!(window.google && window.google.maps));

    useEffect(() => {
        // Define global callback if not exists
        if (!window.initGoogleMaps) {
            window.initGoogleMaps = () => {
                setScriptLoaded(true);
            };
        }

        if (scriptLoaded) return;

        const existingScript = document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`);
        if (existingScript) {
            // Add listener if it's already loading but not done
            existingScript.addEventListener('load', () => setScriptLoaded(true));
            return;
        }

        const script = document.createElement('script');
        // Add callback=initGoogleMaps to URL
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&v=beta&libraries=geometry,marker&loading=async&callback=initGoogleMaps`;
        script.async = true;
        script.defer = true;
        script.onerror = (e) => console.error("Google Maps Script Error:", e);
        document.head.appendChild(script);

        // Cleanup
        return () => {
            // Optional: cleanup global callback if needed, but usually safe to leave
        };
    }, [scriptLoaded]);

    useEffect(() => {
        if (scriptLoaded && mapRef.current && !map) {
            try {
                // Initialize Map
                const googleMap = new window.google.maps.Map(mapRef.current, {
                    center: { lat: 25, lng: 60 }, // Center map to show route approximately
                    zoom: 3, // Start with a broader view
                    mapId: "DEMO_MAP_ID",
                    disableDefaultUI: true,
                    mapTypeId: 'satellite',
                    tilt: 0,
                });

                setMap(googleMap);

                // Add Markers & Path
                const { origin, destination, current, pathPoints } = shipmentData;

                // Origin Marker
                const originPin = new window.google.maps.marker.PinElement({
                    glyph: "HK",
                    background: "grey",
                    borderColor: "white",
                    glyphColor: "white",
                    scale: 0.8
                });
                new window.google.maps.marker.AdvancedMarkerElement({
                    position: origin,
                    map: googleMap,
                    content: originPin.element,
                    title: "Origin: Hong Kong"
                });

                // Destination Marker
                const destPin = new window.google.maps.marker.PinElement({
                    glyph: "HAJ",
                    background: "grey",
                    borderColor: "white",
                    glyphColor: "white",
                    scale: 0.8
                });
                new window.google.maps.marker.AdvancedMarkerElement({
                    position: destination,
                    map: googleMap,
                    content: destPin.element,
                    title: "Destination: Hannover"
                });

                // Route Line (Using Path Points)
                const flightPath = new window.google.maps.Polyline({
                    path: pathPoints,
                    geodesic: true, // Curves between waypoints
                    strokeColor: "#3b82f6",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    icons: [{
                        icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW },
                        offset: '100%',
                        // optional: add repeat to show flow direction along the long path
                        // repeat: '200px' 
                    }]
                });

                flightPath.setMap(googleMap);

                // Auto-zoom disabled
                // Manually fit a rough bound if needed or keep static center
                const bounds = new window.google.maps.LatLngBounds();
                pathPoints.forEach(p => bounds.extend(p));
                googleMap.fitBounds(bounds);
                // googleMap.setZoom(3); // Enforce simpler zoom if bounds is too messy

            } catch (error) {
                console.error("Error initializing map:", error);
            }
        }
    }, [scriptLoaded, map]);

    // Render ships based on products
    useEffect(() => {
        if (!map || !window.google?.maps?.marker) return;

        // Clear existing ship markers
        markersRef.current.forEach(marker => {
            marker.map = null;
        });
        markersRef.current = [];

        // For each product, calculate a position along the route
        const { pathPoints } = shipmentData;
        const totalPoints = pathPoints.length;

        products.forEach((product, index) => {
            // Assign a point on the path based on the product ID or index
            // Distribute them evenly but avoid the very first and very last point if possible
            const pseudoId = product.id || index;
            // Spread between index 2 and totalPoints - 3
            const minIndex = 2;
            const maxIndex = totalPoints - 3;
            let pathIndex = (pseudoId % (maxIndex - minIndex + 1)) + minIndex;

            // In case of a very short path
            if (pathIndex >= totalPoints) pathIndex = totalPoints - 1;

            const position = pathPoints[pathIndex] || shipmentData.current;

            const shipImg = document.createElement('img');
            shipImg.src = 'https://img.icons8.com/color/48/cargo-ship.png';
            shipImg.style.width = '32px';
            shipImg.style.height = '32px';
            shipImg.style.filter = 'drop-shadow(0px 2px 4px rgba(0,0,0,0.5))';

            const tooltipText = `Product: ${product.model_name}\nSKU: ${product.sku}\nETA: ${product.etaFormatted || 'TBA'}`;

            const marker = new window.google.maps.marker.AdvancedMarkerElement({
                position: position,
                map: map,
                content: shipImg,
                title: tooltipText
            });

            markersRef.current.push(marker);
        });

    }, [map, products]);

    return (
        <div className="h-full w-full relative bg-stone-900 rounded-lg overflow-hidden z-10 border border-stone-700">
            <div ref={mapRef} className="w-full h-full" />
            {!scriptLoaded && (
                <div className="absolute inset-0 flex items-center justify-center text-stone-500">
                    Loading Map...
                </div>
            )}
        </div>
    );
};
